import * as React from 'react'
import { Navigate, useOutletContext } from 'react-router-dom'

import { orderBy } from 'lodash'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { isManager } from 'modules/employees/lib/isManager'
import { EnpsFormContext } from 'modules/enps/lib/EnpsFormContext'
import getSelectedFilters from 'modules/enps/lib/getSelectedFilters'
import { AnalyticResults } from 'modules/enps/store/collections/analyticResults'
import type { Cycle } from 'modules/enps/store/models/AnalyticResult'
import type EnpsForm from 'modules/enps/store/models/Form'
import { AnalyticsFilters } from 'modules/surveys/lib/AnalyticsFilters'
import { QuestionGroups } from 'modules/surveys/store/collections/questionGroups'

import ResultsWrapper from './ResultsWrapper'
import { Values, resultsContext } from './useAnalytics'

class DataParams {
  [key: string]: number | number[]
}

interface Props {
  form: EnpsForm
}

const EnpsResultsContextProvider = ({ children, form }: React.PropsWithChildren<Props>) => {
  const { employee } = useCurrentSession()
  const formId = form.attributes.id
  const analyticResults = useLazyRef(() => new AnalyticResults()).current
  const questionGroups = useLazyRef(() => new QuestionGroups()).current
  const [cycle, setCycle] = React.useState<Cycle | null>(null)
  const [requestFilter] = React.useState<AnalyticsFilters>(new AnalyticsFilters(formId))
  const { resultsFilter } = React.useContext(EnpsFormContext)

  const { loading } = useAsyncData(async () => {
    getSelectedFilters(resultsFilter, requestFilter)

    const dataAnalytics: DataParams = {
      surveys_form_id: formId,
    }

    const dataQuestionGroups: DataParams = {
      surveys_form_id: formId,
    }

    if (requestFilter.locations.length) {
      dataAnalytics.locations = requestFilter.locations
    }

    if (requestFilter.teams.length) {
      dataAnalytics.teams = requestFilter.teams
    }

    const canEdit = form.policy.can('editEnpsForm')
    const canSeeAll = canEdit

    if (!canSeeAll && employee && isManager(employee)) {
      dataAnalytics.manager_id = employee.id
    }

    await Promise.all([
      analyticResults.fetch({ data: dataAnalytics }),
      questionGroups.fetch({
        data: dataQuestionGroups,
      }),
    ])

    if (analyticResults.length > 1) {
      throw new Error(
        `AnalyticResults in surveys should only have one result. Survey form id: ${form.id}`
      )
    }

    if (form.hasSchedule && !cycle) {
      const cyclesArray = Array.from(analyticResults.cycles)
      setCycle(cyclesArray[cyclesArray.length - 1] as Cycle)
    }
  }, [formId, resultsFilter])

  const filteredResults = analyticResults.at(0)?.questionGroupsInCycle(cycle) || []
  const filteredResultsCount = filteredResults.length

  let answeredResults = filteredResults.filter((item) => Object.values(item.answers).length > 0)
  // if there are two responses, analytics only shows the last answer per respondent
  answeredResults = orderBy(answeredResults, (item) => [item.created_at, item.answered_at], [
    'desc',
    'desc',
  ])
  const answeredResultsCount = answeredResults?.length || 0

  const values: Values = {
    analyticResults,
    answeredResults,
    answeredResultsCount,
    cycle,
    filteredResults,
    filteredResultsCount,
    form,
    loading,
    setCycle,
  }

  return <resultsContext.Provider value={values}>{children}</resultsContext.Provider>
}

const Results = () => {
  const { employee } = useCurrentSession()
  const { form } = useOutletContext<{ form: EnpsForm }>()
  const canEdit = form.policy.can('editEnpsForm')
  const showResults = canEdit || isManager(employee)
  const { navigation } = useNavigation()

  if (!showResults) return <Navigate to={navigation.engagement.enps.answers.fullPath()} />

  return (
    <EnpsResultsContextProvider form={form}>
      <ResultsWrapper />
    </EnpsResultsContextProvider>
  )
}

export default Results
